import React, { useState } from 'react';
import cogoToast from 'cogo-toast';
import cn from 'classnames';
import { bulkManageUrlData } from '../../api/url_data';
import CatalogToolSubNav from '../CatalogTool/CatalogToolSubNav';
import './BulkManage.scss';

const MAX_LINKS = 1000;

const BulkManage = () => {
  const [links, setLinks] = useState('');
  const [action, setAction] = useState('unlink');
  const [postAction, setPostAction] = useState('requeue');
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);

  const linkCount = links.split('\n').filter(l => l.trim()).length;
  const overLimit = linkCount > MAX_LINKS;

  const handleSubmit = async () => {
    const linkList = links
      .split('\n')
      .map(l => l.trim())
      .filter(l => l.length > 0);

    if (linkList.length === 0) {
      cogoToast.error('Please enter at least one link');
      return;
    }

    if (linkList.length > MAX_LINKS) {
      cogoToast.error(`Too many links to bulk process. Maximum is ${MAX_LINKS} per request.`);
      return;
    }

    setProcessing(true);
    setResults(null);

    try {
      const response = await bulkManageUrlData({
        links: linkList,
        action,
        postAction
      });

      setResults(response.data);
      cogoToast.success(`Processed ${response.data.processed} links`);
    } catch (error) {
      cogoToast.error(error || 'Failed to process links');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className='bulk-manage-container'>
      <header className='bulk-manage-header'>
        <CatalogToolSubNav />
        <h1>Bulk Manage URLs</h1>
      </header>

      <div className='bulk-manage-content'>
        <div className='input-section'>
          <h2>Paste links, one per line</h2>

          <textarea
            value={links}
            onChange={e => setLinks(e.target.value)}
            placeholder='https://example.com/product-1&#10;https://example.com/product-2&#10;https://example.com/product-3'
            rows={15}
            disabled={processing}
          />

          <div className={cn('link-count', { 'over-limit': overLimit })}>
            {linkCount} / {MAX_LINKS} links{overLimit ? ' — over limit' : ''}
          </div>
        </div>

        <div className='options-section'>
          <h2>Action</h2>

          <div className='option-group'>
            <label className='option'>
              <input type='radio' name='action' value='unlink' checked={action === 'unlink'} onChange={e => setAction(e.target.value)} />
              <div className='option-content'>
                <span className='option-title'>Unlink from Product</span>
                <span className='option-description'>Remove the product match from these URLs and update associated pins</span>
              </div>
            </label>
          </div>

          <h2>After Unlinking</h2>

          <div className='option-group'>
            <label className='option'>
              <input
                type='radio'
                name='postAction'
                value='requeue'
                checked={postAction === 'requeue'}
                onChange={e => setPostAction(e.target.value)}
              />
              <div className='option-content'>
                <span className='option-title'>Re-queue for Matching</span>
                <span className='option-description'>Return to matching queue for manual review</span>
              </div>
            </label>

            <label className='option'>
              <input
                type='radio'
                name='postAction'
                value='dismiss'
                checked={postAction === 'dismiss'}
                onChange={e => setPostAction(e.target.value)}
              />
              <div className='option-content'>
                <span className='option-title'>Dismiss</span>
                <span className='option-description'>Mark as dismissed - will not appear in matching queue again</span>
              </div>
            </label>
          </div>

          <div className='action-buttons'>
            <button className='submit-button' onClick={handleSubmit} disabled={processing || !links.trim() || overLimit}>
              {processing ? 'Processing...' : 'Process Links'}
            </button>
          </div>
        </div>

        {results && (
          <div className='results-section'>
            <h2>Results</h2>
            <div className='results-summary'>
              <div className='stat'>
                <span className='stat-value'>{results.processed}</span>
                <span className='stat-label'>Processed</span>
              </div>
              <div className='stat'>
                <span className='stat-value'>{results.pinsUpdated}</span>
                <span className='stat-label'>Pins Updated</span>
              </div>
              <div className='stat'>
                <span className='stat-value'>{results.notFound}</span>
                <span className='stat-label'>Not Found</span>
              </div>
              <div className='stat'>
                <span className='stat-value'>{results.errors}</span>
                <span className='stat-label'>Errors</span>
              </div>
            </div>

            {results.details && results.details.length > 0 && (
              <div className='results-details'>
                <h3>Details</h3>
                <ul>
                  {results.details.map((detail, index) => (
                    <li key={index} className={detail.status}>
                      <span className='link'>{detail.link}</span>
                      <span className='status'>{detail.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkManage;
